/**
 * What the build can carry — the back-engineering ledger.
 *
 * The Web says what the *design* wants (`web.ts`, `epics.ts`); this says what
 * the *simulation* has, and `tools/godhood.ts` prints the difference. That
 * difference is the work list for fitting the sim to the Web, which is the
 * order this was built in on purpose: `DESIGN.md` §14 calls the Web "a
 * requirements map for the simulation", so the map is drawn first and the
 * roads follow it.
 *
 * `test/godhood.ts` asserts that **every variable named by any node, Epic or
 * crossroad appears here**. A new node cannot be added without someone
 * deciding, in writing, whether the simulation can answer it. That is the
 * whole discipline this file exists to enforce.
 *
 * Statuses are deliberately conservative. `built` means a node could read or
 * write it today; `partial` names a real thing to grow from; `absent` means
 * nothing corresponds, and when in doubt it is `absent`. Every `where` names
 * a file or a constant, never a hope.
 *
 * Pure by law 7.
 */

import type { Binding } from './types';

const built = (where: string): Binding => ({ status: 'built', where });
const partial = (where: string): Binding => ({ status: 'partial', where });
const absent = (where: string): Binding => ({ status: 'absent', where });

export const BINDINGS: Readonly<Record<string, Binding>> = {

  /* ── the divine act: the best-served corner of the Web ───────────────── */

  'act.cost': built('leverage.ts costOf — base × focus × reach × environment'),
  'act.work': built('simulation.ts act — multiplies the work of every cast after the charge (2026-08-18)'),
  'act.work.tightFocus': built('simulation.ts concentrationFor, up to 3.4× at a channelled cast'),
  'act.work.wideFocus': built('concentrationFor falls as area^0.55'),
  'act.cost.tightFocus': built('leverage.ts focusFactor, FOCUS_EXP 0.9'),
  'act.work.highViability': built('leverage.ts processViability'),
  'act.work.lowViability': built('leverage.ts VIABILITY_FLOOR 0.25 — a quarter effect, never zero'),
  'act.cost.highViability': partial('environmentFactor prices the land; it does not band by viability'),
  /** The call site is WRITTEN and waiting: `processViability` takes a `floor`
   *  mod and `Simulation.act` passes the other two beside it. What is missing
   *  is the channel — this is an ADD and can legitimately be negative (an Epic
   *  sells −0.15), and `mod()`'s guard rewrites any non-positive to 1. It
   *  needs `bonus()`, which needs an injection surface, which waits on the
   *  first buyable `add` effect. `WIRING.md` A1. */
  'process.viabilityFloor': partial('leverage.ts processViability takes it; the bonus channel to carry it does not exist yet'),
  'resistance.effect': partial('environmentFactor is the cost of working against the land'),
  'impulse.cost.distance': built('leverage.ts reachFactor, REACH_SCALE 2600, REACH_EXP 1.15'),
  'impulse.regen': built('anchor.ts POWER_PER_DAY 0.35 on the day grid (0.02 until 2026-08-18)'),
  'act.cost.familiarGround': partial('reachFactor already takes anchorNear; no resonance term reads it'),
  'act.work.repeated': partial('things.ts counts repeats as standing; nothing reads it back as efficiency'),
  'act.work.repeated.lapse': partial('things.ts standing decays on a 25-year half-life'),
  'act.collateralArea': absent('a cast is one disc; there is no separate collateral footprint'),
  'act.collateralMagnitude': absent('and no collateral magnitude to reduce'),
  'act.cost.sustained': built('simulation.ts act — the price of a held cast\'s stones past the first (2026-08-18)'),
  'act.work.burst': built('simulation.ts act — the work of a one-stone (tapped) cast (2026-08-18)'),
  'act.impulseShare': built('main.ts chargeStones — the share of the god a hold may gather; the base already allows all of it, so the epic\'s cap is satisfied at baseline (2026-08-18)'),
  'act.work.leveraged': absent('no detection of a high-leverage causal point'),
  'act.work.propagation': absent('processes expose no dependencies to propagate along (D §14.3)'),
  'act.work.propagationHops': absent('same'),
  'act.work.remote': absent('acting is always at the presence'),
  'act.remote.knownOnly': absent('same'),
  'act.work.unwitnessed': absent('nothing reads witness count into the work done'),
  'impulse.cost.altitude': absent('altitude is free; presence.ts caps it and never charges for it'),
  'impulse.cost.lowViability': partial('processViability scales the effect, not the price'),
  'impulse.cost.mortalTarget': absent('costOf does not read the target kind'),
  'impulse.cost.unwitnessed': absent('nor whether anyone is watching'),
  'impulse.regen.floor': absent('there is no Faith for a floor to be independent of'),
  'travel.cost.horizontal': absent('flight is free — presence.ts spends nothing to move'),
  'focus.capOverride': absent('focusCap is hard'),
  'focus.maxRadius.fromAltitude': partial('main.ts focusCap is reach-only; D §3.3 adds the altitude term — ROADMAP §17.5 item 3'),
  'focus.maxRadius.fromResonance': absent('resonance does not reach the focus cap'),

  /* ── grasp: built, and priced by physics (DESIGN-IV §21–27) ──────────── */

  'grasp.maxMass': built('leverage.ts GRASP_MASS_EXP 1.25 — the ceiling is implicit in the curve'),
  'grasp.cost.attachment': built('leverage.ts graspCost(massKg, attachment)'),
  'grasp.damage': built('leverage.ts graspDamage(attachment, living)'),
  'grasp.precision': absent('sim.grasp places an item; there is no throw or aim'),

  /* ── the presence ────────────────────────────────────────────────────── */

  'presence.speed': built('presence.ts — speed granted by clearance'),
  'presence.speed.fromAltitude': built('same: the altitude term is the whole speed model'),
  'presence.maxAltitude': built('god.ts maxAltitude, interpolated along the reach ladder'),
  /** WAS `built` against the free flight's half-second ground lookahead, which
   *  the rule turned off. The orbit camera (2026-08-17) has no lookahead: the
   *  target skims the land and the eye is lifted clear. `main.ts` says so where
   *  it used to set it. An orbit-era meaning — a lower elevation floor, an eye
   *  allowed nearer the ground — is a ruling, not a wire. */
  'presence.terrainConstraint': partial('ui/presence.ts — the orbit eye has no ground lookahead for the rule to turn off; wants an orbit-era meaning'),
  'presence.comfortableAltitude': partial('god.ts maxAltitude is a hard ceiling; D §3.2 wants a soft one to drift back to'),
  'perception.distance': partial('attention.ts tolerance scales with distance; it is not a deity stat'),
  'perception.objectOfCare.range': absent('follow drops the camera to the person; range is not modelled'),
  'perception.systemResolution': absent('altitude reveals no aggregate reading of the world'),

  /* ── resonance, anchors, and the land's memory ───────────────────────── */

  /**
   * **This said `built` and it was not.** `anchor.ts` sets `resonance: 1` at
   * `place()` and the only other write is `decay()`. Nothing raises it, ever —
   * there is no gain to have a rate. Marked honestly rather than given a fake
   * reader, because `tools/godhood.ts`'s readiness score trusts this field and
   * a wrong `built` is worse than an `absent`: it says the work is done.
   */
  'resonance.gainRate': absent('anchor.ts sets resonance to 1 at placement and only decays it — there is no gain path'),
  'resonance.decayRate': built('anchor.ts — 200-year half-life, untouchable by mortals'),
  'anchor.strength': built('anchor.ts AnchorState + the footpath→temple ladder'),
  'env.effect.decayRate': built('world/env.ts — exponential decay of every deviation'),
  'life.effect.decayRate': built('env.ts, on the positive biomass deviation Strengthen writes'),
  'things.standing.decayRate': partial('world/things.ts 25-year half-life — but a ThingMark carries no godId, so there is no per-deity route to it'),
  'history.traceLegibility': partial('wear and things exist; nothing modulates how readable they are'),
  'resonance.legible': absent('resonance is never surfaced to the player in any form'),
  /** Wired 2026-08-17: `main.ts`'s Chronicle walk exempts events at your own
   *  stones from the distance filter — the place goes on reporting after the
   *  god has gone, which is what attendance buys. */
  'anchor.perception': built('main.ts noteEvents — your own stones pass the awareness gate'),
  'anchor.network': absent('anchors are independent; there is no network between them'),
  'homeGround.efficiency': absent('no notion of ground being *yours*'),
  'heartland.outside': absent('same — no heartland to be outside of'),
  'place.sense.state': absent('a place cannot be asked what has happened to it'),

  /* ── aura: built. ROADMAP §16 said "nothing continuous exists"; it does now,
      carried by the Anchor exactly as that section predicted. What is still
      missing is the *shaping* of it — afterglow and attunement — not the
      thing itself. ────────────────────────────────────────────────────── */

  'aura.work': built('aura.ts — a week\'s share × processViability, on the day grid'),
  'aura.strength': built('aura.ts AURA_DAILY_SHARE — 1/55 of a reference cast a day'),
  'aura.radius': built('aura.ts AURA_RADIUS, 180 m'),
  'aura.upkeep': built('aura.ts AURA_UPKEEP_PER_DAY 0.105 — three auras consume a god (0.006 until 2026-08-18)'),
  /** A **rule**, and wiring it would GATE something that currently works:
   *  auras are available to every god today, and this would put them behind a
   *  qualified Root mastery node. That is a ruling about what the game is, not
   *  a call site — so it stays unwired and honestly marked. */
  'aura.fromAnchor': partial('aura.ts is built and ungated; the rule would make it a Root purchase, which is a design ruling'),
  'aura.afterglow.strength': absent('nothing persists in a place after the god leaves it'),
  'aura.afterglow.halfLife': absent('same'),
  'aura.afterglow.count': absent('same'),
  'aura.attunement.count': absent('no process categories to attune to'),
  'aura.work.attuned': absent('same'),
  'aura.work.unattuned': absent('same'),
  'aura.crossProcess': absent('same'),

  /* ── mortals: society is real, institutions are not ──────────────────── */


/**
 * ── THE VARIABLES THAT HAVE NO DEITY TO ASK ─────────────────────────────
 *
 * Twenty-one of the `built` entries below were marked so honestly — the
 * machinery is real, named, and running — and yet **none of them can be
 * wired**, for a reason that has nothing to do with effort. It is worth
 * writing down once, here, rather than being rediscovered per variable.
 *
 * The Web is **per-deity**. Every read seam takes a `deityId`, because a
 * rival god in the same world must get its own answer. And the systems these
 * variables name — a band's cohesion, a people's drift, a herd's recovery, a
 * mark's decay — **belong to no god**. There is no `deityId` in scope at any
 * of those sites, and there is no honest one to invent:
 *
 *  · A band is not owned by a god. Asking *whose modifier applies to this
 *    band* has no answer, and the tempting one — the local player's — makes
 *    the entire world behave according to one god's build, which stops being
 *    true the moment there are two.
 *  · `src/sim/mortals/` **may not so much as name a `godId`**, and
 *    `test/deity.ts` walks every file under it and fails if one does. That is
 *    law 10 made mechanical: two gods doing the same thing have to be
 *    indistinguishable to every eye in the world, and a band that could ask
 *    which god was nearby would be an eye that can tell.
 *  · `ThingMark` carries no god either, by the same design, so *your marks
 *    last longer* has nothing to attach to.
 *
 * So these are **`partial`**: the simulation half exists and is exactly where
 * the note says, and what is missing is a per-deity route to it that does not
 * breach the wall. That is a design question — should a god's build reach
 * mortals at all, and if so through what — and not a call site somebody
 * forgot to write.
 *
 * What CAN be wired, and now is: anything a god owns or does. Acts, focus,
 * viability, grasp, the presence, its own anchors, its own auras, and the
 * salience of its own events. The pattern is exact — **a variable is
 * reachable exactly when the thing it modifies has a god attached to it.**
 */

  'band.cohesion': partial('band.ts — a target set by conditions, tracked over a fortnight; no deityId in scope and sim/mortals may not name one'),
  'fission.propensity': partial('band.ts split — under 0.36 for 45 days at 34 people; no deity to ask'),
  'fission.threshold': partial('same, and the same reason'),
  'memory.band.halfLife': partial('band.ts MEMORY_HALFLIFE and hold(); no deity to ask'),
  'memory.band.spread': partial('deliverNews carries stories home; no deity to ask'),
  'memory.person.halfLife': partial('PersonMemory exists, capped at 10 and evicted by decayed importance; no half-life of its own'),
  'person.importance': partial('people.ts field, raised by close divine witnessing and read by the card; the WRITE has a deity, the field does not'),
  'mortal.labour': partial('band.ts party work rates; no deity to ask'),
  'structure.maintenance': partial('construction.ts condition decay; no deity to ask'),
  'pressure.resolution.mortal': partial('decisions.ts Pressures is exactly what a band wants; nothing resolves it for them'),
  'decision.localWeight': partial('decisions.ts scores per band; there is no authority to weigh against'),
  'person.promotion': partial('importance exists; there is no promoted-person entity (D §11.2)'),
  'conflict.initiation': absent('bands do not fight'),
  'collapse.magnitude': absent('fission is a split, not a collapse with a size'),
  'mortal.labour.coerced': absent('there is no way to compel anybody'),
  'institution.authority': absent('no institutions or polities at all (D §14.3)'),
  'institution.successionFailure': absent('same'),
  'institution.crisisDuration': absent('same'),
  'institution.memoryHalfLife': absent('same'),
  'institution.innovation': absent('same'),
  'institution.recovery': absent('same'),
  'pressure.destabilise.weak': absent('no institutional stability to destabilise'),
  'pressure.destabilise.strong': absent('same'),
  'event.salience.revolt': absent('same'),

  /* ── culture and practice: the strongest-bound corner ────────────────── */

  'culture.divergence': partial('culture.ts drift; a people belongs to no god'),
  'practice.driftRate': partial('culture.ts practice drift; no deity to ask'),
  'practice.transmission': partial('culture.ts inheritance on fission; no deity to ask'),
  'practice.adoption': partial('culture.ts; no deity to ask'),
  'pattern.inheritance': partial('people.ts makeChild; no deity to ask'),
  'innovation.rate': partial('construction.ts; no deity to ask'),
  'innovation.cultural': partial('culture.ts; no deity to ask'),
  'culture.driftOnFission': partial('daughters inherit whole; there is no fission-specific identity loss'),
  'pattern.persistence': partial('paths.ts strength persists; rites are practices with a drift rate'),
  'religion.transmission': absent('religion is deliberately not an entity (D §13.1, and test/culture.ts asserts it)'),

  /* ── faith: does not exist ───────────────────────────────────────────── */

  'faith.contribution': absent('there is no Faith; anchor regard is place-scoped and is not it'),
  'faith.fromMemory': absent('same'),
  'faith.fromMisattribution': absent('same'),
  'faith.fromStewardship': absent('same'),
  'faith.fromUnwitnessed': absent('same'),
  'faith.hostile': absent('same'),
  'communion.benefit': absent('same'),
  'perception.viaBelievers': absent('no believers to report anything'),
  'perception.viaBelievers.detail': absent('same'),
  'perception.reportLatency': absent('same'),
  'perception.reportAccuracy': absent('same'),

  /* ── omens and witnessing ────────────────────────────────────────────── */

  'omen.uncertainty.mortal': partial('omen.ts omenUncertainty; the SIGN has a deity, the band reading it may not know one exists'),
  'omen.override.drives': partial('decisions.ts spareAttention gates every non-survival row; no deity to ask'),
  'omen.responseLatency': partial('investigate is a real activity; latency is the 6-hour replan grid'),
  'event.salience.divine': built('events.ts salience, scaled by cast radius'),
  'omen.uncertainty.animal': absent('omens reach bands; wildlife never hears them'),
  'memory.animal.halfLife': absent('animals remember nothing'),
  'witness.fear': absent('witnesses record an event; they do not feel about it'),

  /* ── life, death and ecology ─────────────────────────────────────────── */

  'habitat.carryingCapacity': partial('social/habitat.ts; the land belongs to no god'),
  'wildlife.reproduction': partial('populations.ts growthPerDay; no deity to ask'),
  'population.recovery': partial('populations.ts; no deity to ask'),
  'succession.onset': partial('world/sere.ts STAGE_AGES are fixed at 1/6/25/80 years'),
  'succession.recoveryRate': partial('same — the sere runs on age, not on a rate anything can bias'),
  'nutrient.return': partial('wildlife carrion exists (populations.ts); there is no soil nutrient field'),
  'mortality.stress': partial('mortality.ts is a Siler hazard by age; starvation and thirst are separate'),
  'mortality.healthy': partial('the hazard reads age, never condition'),
  'reproduction.rate': partial('bands grow on surplus; plants are a pure function of position and do not reproduce'),
  'mortality.selectivity': absent('the hazard cannot prefer the poorly adapted — there is no adaptation'),
  'decomposition.rate': absent('sere.ts ages fuel; nothing decomposes as a rate'),
  'death.work.direct': absent('no verb kills anything directly'),
  'death.work.healthyTarget': absent('same'),
  'decline.duration': absent('nothing declines — it is alive or it is not'),
  'organism.reserve': absent('plants carry no reserve to draw on'),
  'growth.burst': absent('no burst/steady split in growth'),
  'recovery.individual': absent('individual organisms have no state to recover'),
  'dispersal.range': absent('plants are a pure function of position; nothing spreads'),
  'establishment.marginal': absent('same'),
  'life.work.propagation': absent('no dependency links between organisms'),
  'life.work.monoculture': absent('no community-composition reading'),
  'adaptation.pressure': absent('no heritable traits to adapt'),
  'adaptation.variance': absent('same'),
  'adaptation.abrupt': absent('same'),
  'lineage.traitVariation': absent('people have bornAt but no parents; nothing inherits'),
  'lineage.salienceInheritance': absent('same'),
  'lineage.imprintInheritance': absent('same'),

  /* ── beasts ──────────────────────────────────────────────────────────── */

  'migration.routePersistence': partial('wildlife/migration.ts moves populations and paths.ts persists mortal routes; neither is a named route'),
  'herd.cohesion': absent('wildlife is per-cell populations — there is no herd entity'),
  'animal.promotion': absent('things.ts promotes places; animals cannot be promoted'),
  'beast.work.individual': absent('no animal is an individual to the simulation; a herd press cannot single one out'),
  'beast.work.group': absent('same — there is no group under the cell population to press separately'),
  'beast.work.literacy': absent('nothing reads a target kind into how hard a verb lands'),

  /**
   * ── THE BEASTS DIRECTION (2026-08-17) ───────────────────────────────────
   *
   * Every rule below that says `built` has a consumer outside `godhood/` and
   * `test/wiring.ts` §8 refuses one that has not. The read rules are consumed
   * by `sim/wildlife/reading.ts` through `main.ts`'s card (`cardFor`, the
   * herd and animal cases) and phrased by `ui/beasts.ts`; the press rules by
   * `Simulation.act` writing `WildlifeSystem.press` and by the readers inside
   * `populations.ts` / `migration.ts`. Every one of them reads STATISTICAL
   * truth — a cell's population of one kind — because that is all the
   * simulation has of an animal; nothing here claims an individual, a group,
   * a memory or a signal, and the `absent` rows say so in words the panel
   * prints.
   */

  // reading — the awakening and inclination layers
  'beast.read.drive': built('wildlife/reading.ts driveOf — what a cell\'s herd is doing about its ground, off pop/cap/prevCap and the migration predicate'),
  'beast.read.company': built('wildlife/reading.ts — head class, and how many of the neighbouring cells hold the kind'),
  'beast.read.range': built('wildlife/reading.ts rangeOf — presence across the survey ring, edge-of-range, ground lately gone from'),
  'beast.read.reach': built('wildlife/reading.ts — the survey ring, 1 + bonus(); the first reader `bonus()` has ever had (WIRING A1)'),
  'beast.trace.movement': built('wildlife/reading.ts movementOf — recent wildlife_migrated events for this cell and kind, off the events ring'),
  'beast.compare': built('wildlife/reading.ts compareOf — this cell against the neighbours holding the same kind: density, trend, wear'),
  'beast.read.season': built('wildlife/reading.ts — seasonalDryness × (1 − waterAccess), the same term populations.capacity applies'),
  'beast.read.carrion': built('wildlife/reading.ts — the cell\'s carrion pool against what the scavengers\' K reads from it'),
  'beast.read.individual': absent('no animal is an individual to the simulation — the drawn body is a render stand-in (render/wildlife.ts header)'),

  // practice — trace, the human edge, and the first Beast ACT
  'beast.trace.prey': built('wildlife/reading.ts preyOf — a predator cell\'s prey head against PREDATOR_SUPPORT; a prey cell\'s hunters and their daily take'),
  'beast.read.mortal': built('wildlife/reading.ts — hunting parties standing on the cell (main.ts hands them in) and predator_incident events near it'),
  // the one Become moment (`PLAYABLE.md` P5, 2026-08-22): Strengthen on a
  // person reaches what they lately became
  'press.reach.becoming': built('simulation.ts act → BandSystem.press(…, reachBecoming) → people.ts press reads the person\'s latest becoming into the asked rows, in the direction they became it'),
  'beast.press.movement': built('simulation.ts act → WildlifeSystem.press; migration.ts reads the press into the bar and the moved fraction'),
  'beast.press.increase': built('populations.ts daily — the logistic growth term reads a positive press while the herd is under its K'),
  'beast.press.hunt': built('populations.ts daily — the predation intake reads a positive press on the predator cell'),
  'beast.work.movement': built('simulation.ts act — the delivered press is scaled by it'),
  'impulse.cost.beastTarget': built('simulation.ts act — costOf\'s bill × this when the target is a herd or an animal'),
  'beast.omen.audience': absent('omens reach bands; wildlife never hears them (omen.ts)'),
  'beast.remember.lineage': absent('no animal has parents or young'),

  // mastery — larger subjects
  'beast.subject.population': built('wildlife/reading.ts populationOf — the connected ground around a cell holding the kind; main.ts resolves it past the herd\'s own footprint'),
  'beast.read.territory': built('wildlife/reading.ts — the population\'s ground: cells lately gone from (gone), cells returned to, cells shared with predators'),
  'beast.read.branch': built('wildlife/reading.ts branchOf — a herd near the migration bar or its K near the DECLINED trigger'),
  'beast.press.persistence': built('populations.ts daily — the press decays on PRESS_HALFLIFE × this'),
  'beast.subject.lineage': absent('no animal has kin'),
  'beast.subject.culture': absent('no animal learns; there is no per-population behavioural state of any kind'),
  'beast.read.memory': absent('animals remember nothing'),

  // transcendence — awareness, role, the wide hand; and the Blood and Voice
  // outer nodes, written and honestly absent
  'beast.aware.wild': built('main.ts noteEvents — great migrations, vanishings and returns pass the awareness gate for a god who holds it'),
  'beast.trace.role': built('wildlife/reading.ts roleOf — the K decomposition populations.capacity actually applies: sere shares, the season\'s cure, range wear, water'),
  'beast.press.movement.wide': built('simulation.ts act — a wide cast (aimed:false) presses every inclined herd in the disc'),
  'beast.work.movement.wide': built('simulation.ts act — the wide press is scaled by it'),
  'beast.care.lineage': absent('no animal has kin, so there is no line to keep'),
  'beast.culture.persistent': absent('no animal learns'),
  'beast.omen.carried': absent('wildlife hears no sign and carries nothing'),
  'beast.press.heritable': absent('world/strain.ts is complete and deliberately inert (WIRING B1); waking it is parked'),
  'beast.omen.recognised': absent('wildlife never hears an omen'),

  /* ── insight and rivalry ─────────────────────────────────────────────── */

  'foresight.horizon': absent('no causal preview of any kind'),
  'foresight.branches': absent('same'),
  'rivalInfluence.effect': absent('gods share a pantheon and never interact'),
};
